import { Link } from "react-router-dom";
import { ArrowRight, Sparkle, Heart, Eye } from "@phosphor-icons/react";

const Home = () => {
  const featuredServices = [
    {
      title: "Facial Balancing",
      description: "Artistically designed treatments to harmonize your natural features",
      icon: <Eye size={32} weight="light" />,
    },
    {
      title: "Injectable Artistry",
      description: "Precision techniques for subtle, natural-looking enhancement",
      icon: <Sparkle size={32} weight="light" />,
    },
    {
      title: "Skin Rejuvenation",
      description: "Advanced protocols for luminous, youthful skin",
      icon: <Heart size={32} weight="light" />,
    },
  ];

  return (
    <div data-testid="home-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-smoke via-smoke/95 to-transparent z-10" />
          <img
            src="https://images.unsplash.com/photo-1727386245768-a284a5fc1548?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Artistic portrait showcasing facial aesthetics"
            className="w-full h-full object-cover object-center grayscale"
          />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-20">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-6 animate-fade-in">
              Sculpted Beauty Studio
            </p>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[0.9] text-matte animate-fade-in-up">
              The Art of
              <br />
              <span className="italic">Natural</span> Beauty
            </h1>
            <p className="mt-8 text-base md:text-lg font-light text-matte/70 leading-relaxed max-w-md animate-fade-in-up delay-200">
              Where artistry meets medical precision. Customized facial design plans 
              crafted to enhance your unique features.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-300">
              <Link
                to="/contact"
                data-testid="hero-book-btn"
                className="btn-primary inline-flex items-center justify-center gap-3"
              >
                Book Consultation
                <ArrowRight size={16} weight="light" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-btn"
                className="btn-secondary inline-flex items-center justify-center"
              >
                Explore Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 md:py-32 lg:py-40 bg-cream">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="order-2 lg:order-1">
              <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
                Our Philosophy
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight leading-none text-matte">
                Artistry in Every Detail
              </h2>
              <p className="mt-8 text-base font-light text-matte/70 leading-relaxed">
                At Medical Aesthetic Arts, we believe beauty is a form of self-expression. 
                Our approach combines the precision of medical science with the creativity 
                of artistic vision, crafting results that honor your individuality.
              </p>
              <p className="mt-6 text-base font-light text-matte/70 leading-relaxed">
                Each treatment plan is thoughtfully designed, considering facial proportions, 
                symmetry, and your personal aesthetic goals. The result is enhancement that 
                feels authentically you.
              </p>
              <Link
                to="/about"
                data-testid="philosophy-learn-more"
                className="btn-secondary inline-flex items-center mt-10"
              >
                Learn More About Us
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <div className="image-reveal aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/30853594/pexels-photo-30853594.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Classical sculpture representing artistic beauty"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-24 md:py-32 lg:py-40 bg-smoke">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16 md:mb-24">
            <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
              Our Expertise
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
              Featured Services
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredServices.map((service, index) => (
              <div
                key={service.title}
                data-testid={`service-card-${index}`}
                className="luxury-card group cursor-pointer"
              >
                <div className="text-taupe mb-8 group-hover:text-matte transition-colors duration-300">
                  {service.icon}
                </div>
                <h3 className="font-serif text-2xl md:text-3xl font-light text-matte mb-4">
                  {service.title}
                </h3>
                <p className="text-sm font-light text-matte/60 leading-relaxed">
                  {service.description}
                </p>
                <div className="mt-8 flex items-center gap-2 text-taupe group-hover:text-matte transition-colors duration-300">
                  <span className="text-xs uppercase tracking-[0.15em]">Learn More</span>
                  <ArrowRight size={14} weight="light" />
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link
              to="/services"
              data-testid="view-all-services"
              className="btn-primary inline-flex items-center gap-3"
            >
              View All Services
              <ArrowRight size={16} weight="light" />
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 md:mb-16 gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
                Transformations
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
                Before & After
              </h2>
            </div>
            <Link
              to="/gallery"
              data-testid="view-gallery-btn"
              className="btn-secondary inline-flex items-center"
            >
              View Full Gallery
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="image-reveal aspect-[3/4] overflow-hidden">
                <img
                  src={`https://images.unsplash.com/photo-1727386245813-c70d8883995e?crop=entropy&cs=srgb&fm=jpg&q=85&w=${300 + item * 50}`}
                  alt={`Transformation result ${item}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIP Preview */}
      <section className="vip-section py-24 md:py-32 lg:py-40 relative">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
                Exclusive Access
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-smoke">
                VIP Membership
              </h2>
              <p className="mt-8 text-base font-light text-smoke/60 leading-relaxed">
                Experience the pinnacle of personalized aesthetic care. Our VIP members 
                enjoy priority scheduling, exclusive treatments, and bespoke skincare 
                protocols designed just for them.
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  "Priority booking & extended appointments",
                  "Exclusive member-only treatments",
                  "Complimentary skincare consultations",
                  "Special pricing on products & services",
                ].map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3 text-sm text-smoke/60">
                    <span className="w-1.5 h-1.5 bg-taupe rounded-full" />
                    {benefit}
                  </li>
                ))}
              </ul>
              <Link
                to="/vip-membership"
                data-testid="vip-learn-more"
                className="mt-10 inline-flex items-center gap-3 border border-taupe text-smoke bg-transparent hover:bg-taupe hover:text-matte uppercase tracking-[0.2em] text-xs font-medium px-8 py-3 transition-all duration-300"
              >
                Discover VIP Benefits
                <ArrowRight size={16} weight="light" />
              </Link>
            </div>
            <div className="hidden lg:block">
              <div className="image-reveal aspect-square overflow-hidden border border-smoke/10">
                <img
                  src="https://images.unsplash.com/photo-1677568554453-ae5baf28da6c?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Luxury aesthetic clinic interior"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-smoke">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-matte">
            Begin Your Transformation
          </h2>
          <p className="mt-6 text-base font-light text-matte/60 max-w-lg mx-auto">
            Schedule your personalized consultation and discover how we can help you 
            achieve your aesthetic goals.
          </p>
          <Link
            to="/contact"
            data-testid="cta-book-btn"
            className="btn-primary inline-flex items-center gap-3 mt-10"
          >
            Book Your Consultation
            <ArrowRight size={16} weight="light" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
